import React, { useRef, useState } from 'react'
import { makeStyles, Popover } from '@material-ui/core'
import Icon from '../../../atoms/Icon'
import { useDDPInspectorContext } from './DDPInspectorContext'

const useStyles = makeStyles(() => ({
  button: {
    position: 'fixed',
    right: 16,
    bottom: 16,
    zIndex: 1300, // above MUI Drawer modal layer (1200) so it stays clickable
    width: 44,
    height: 44,
    borderRadius: '50%',
    border: 'none',
    // Mirrors the Denali AI logo gradient (`#denali-ai-gradient`) so the
    // inspector affordance reads as the same family of internal tooling.
    background: 'linear-gradient(135deg, var(--palette-purple-500) 0%, var(--palette-blue-500) 100%)',
    color: 'white',
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 11,
    fontWeight: 700,
    letterSpacing: '0.5px',
    cursor: 'pointer',
    boxShadow: '0 4px 12px rgba(76, 29, 149, 0.25)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease',
    '&:hover': {
      transform: 'translateY(-1px)',
      boxShadow: '0 6px 18px rgba(76, 29, 149, 0.35)',
      filter: 'brightness(1.05)'
    }
  },
  badge: {
    position: 'absolute',
    top: -4,
    right: -4,
    minWidth: 18,
    height: 18,
    padding: '0 5px',
    borderRadius: 9,
    backgroundColor: 'var(--color-neutral-900, #111)',
    color: 'white',
    fontSize: 10,
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
  },
  popover: {
    minWidth: 320,
    maxWidth: 480,
    maxHeight: '60vh',
    overflowY: 'auto'
  },
  header: {
    padding: '10px 14px',
    borderBottom: '1px solid var(--color-neutral-200)',
    fontSize: 11,
    fontWeight: 700,
    color: 'var(--color-neutral-500)',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    backgroundColor: 'var(--color-neutral-050)'
  },
  empty: {
    padding: 20,
    textAlign: 'center',
    color: 'var(--color-neutral-400)',
    fontSize: 13,
    fontStyle: 'italic'
  },
  entry: {
    width: '100%',
    padding: '10px 14px',
    border: 'none',
    background: 'white',
    textAlign: 'left',
    cursor: 'pointer',
    borderBottom: '1px solid var(--color-neutral-100)',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    '&:hover': {
      backgroundColor: 'var(--color-neutral-050)'
    }
  },
  status: {
    width: 8,
    height: 8,
    borderRadius: '50%',
    flexShrink: 0
  },
  statusLoading: { backgroundColor: 'var(--color-warning-500, #f59e0b)' },
  statusSuccess: { backgroundColor: 'var(--color-success-500, #10b981)' },
  statusError: { backgroundColor: 'var(--color-danger-500, #ef4444)' },
  body: {
    flex: 1,
    minWidth: 0
  },
  name: {
    fontSize: 13,
    fontWeight: 600,
    color: 'var(--color-neutral-800)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  meta: {
    fontSize: 11,
    color: 'var(--color-neutral-500)',
    fontFamily: 'var(--font-mono, monospace)'
  }
}))

const STATUS_CLASS = {
  loading: 'statusLoading',
  success: 'statusSuccess',
  error: 'statusError'
}

export default function DDPInspectorLauncher () {
  const classes = useStyles()
  const { entries, openDrawerFor } = useDDPInspectorContext()
  const buttonRef = useRef(null)
  const [open, setOpen] = useState(false)

  if (entries.length === 0) return null

  const handleSelect = (id) => {
    setOpen(false)
    openDrawerFor(id)
  }

  return (
    <>
      <button
        ref={buttonRef}
        type='button'
        className={classes.button}
        onClick={() => setOpen(o => !o)}
        title='DDP Inspector'
        aria-label='Open DDP Inspector'
      >
        <Icon name='search' customSize={22} />
        <span className={classes.badge}>{entries.length}</span>
      </button>
      <Popover
        open={open}
        anchorEl={buttonRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{ className: classes.popover }}
      >
        <div className={classes.header}>
          DDP Inspector — {entries.length} active
        </div>
        {entries.map(entry => {
          const variant = STATUS_CLASS[entry.status] || 'statusSuccess'
          const stepLabel = typeof entry.pipeline === 'string'
            ? entry.pipeline
            : `inline (${entry.pipeline?.length ?? 0} steps)`
          return (
            <button
              key={entry.id}
              type='button'
              className={classes.entry}
              onClick={() => handleSelect(entry.id)}
            >
              <span className={`${classes.status} ${classes[variant]}`} />
              <span className={classes.body}>
                <span className={classes.name}>{entry.displayName}</span>
                <br />
                <span className={classes.meta}>{stepLabel}</span>
              </span>
            </button>
          )
        })}
      </Popover>
    </>
  )
}
